import { isActive } from './agenda'
import { OWNER_STAFF_ID, isOwnerId, salonDayFor, staffName } from './salonDays'
import type { AgendaAppointment } from '../types/agenda'
import type { OpeningDay, SalonDayException, StaffProfile } from '../types/site'

/**
 * Afwezigheid op één dag ("Mila is ziek"), docs/KASSA-ABONNEMENTEN.md §4. Puur: dezelfde regels
 * voor het scherm (wat er gaat gebeuren) en de demo-server (wat er gebeurt).
 *
 * - `naar-eigenaar`: Sarah neemt de dag over. De dag wordt een afwijkende dag met Sarah en dezelfde
 *   tijden; alle open afspraken van die dag gaan naar haar (zelfde tijd, de klant krijgt bericht).
 * - `afzeggen`: de salon is die dag dicht; alle open afspraken worden geannuleerd en de klanten
 *   krijgen de mail "Afspraak gaat niet door" met een link om te verzetten.
 */
export type AbsenceAction = 'naar-eigenaar' | 'afzeggen'

export type AbsencePlan = {
  /** Open afspraken van die persoon op die dag (niet afgerond, niet geannuleerd). */
  affected: AgendaAppointment[]
  /** Kan Sarah overnemen? Niet als Sarah zelf afwezig is. */
  ownerCanTakeOver: boolean
  /** Waarom afwezig melden (nog) niet kan; `null` = kan. */
  blocked: string | null
}

export function absencePlan(
  appointments: readonly AgendaAppointment[],
  date: string,
  staffId: string,
  hours: readonly OpeningDay[],
  exceptions: readonly SalonDayException[] | undefined,
  todayIso: string,
): AbsencePlan {
  const day = salonDayFor(hours, exceptions, date)
  const affected = appointments.filter((a) => a.date === date && (a.staffId || OWNER_STAFF_ID) === staffId && isActive(a))
  const blocked = date < todayIso ? 'Deze dag is al voorbij.' : !day ? 'De salon is deze dag al dicht.' : day.staffId !== staffId ? 'Zij werkt deze dag niet.' : null
  return { affected, ownerCanTakeOver: !isOwnerId(staffId), blocked }
}

/** De afwijkende dag die bij de keuze hoort. Vervangt een eventuele eerdere voor die datum. */
export function absenceException(
  day: { date: string; open: string; close: string },
  absentId: string,
  action: AbsenceAction,
  staff: readonly StaffProfile[],
  id: string,
): SalonDayException {
  const name = staffName(staff, absentId)
  return action === 'naar-eigenaar'
    ? { id, date: day.date, open: day.open, close: day.close, note: `${name} afwezig, ${staffName(staff, OWNER_STAFF_ID)} neemt over` }
    : { id, date: day.date, note: `${name} afwezig, afspraken afgezegd` }
}

/** Uitzonderingen met deze erbij; een eerdere op dezelfde datum vervalt. Gesorteerd op datum. */
export const withException = (list: readonly SalonDayException[] | undefined, ex: SalonDayException): SalonDayException[] =>
  [...(list ?? []).filter((e) => e.date !== ex.date), ex].sort((a, b) => a.date.localeCompare(b.date))
